import { PathologyLab, MSOPractice } from '../types';

// Unified pathology filter definitions with category labels
export const pathologyFilters = {
  // Combined list for display — strategy filters, then platform criteria, then add-on criteria
  all: [
    // Investment Strategy Filters (top priority)
    { id: 'strategy-platform', label: 'Platform Candidates', priority: 0, category: 'strategy' },
    { id: 'strategy-addon', label: 'Add-on Candidates', priority: 0, category: 'strategy' },

    // Platform Criteria
    { id: 'revenue-platform', label: 'Revenue $1.5M-$9M (target range)', priority: 1, category: 'platform' },
    { id: 'multi-location', label: 'Multi-location potential or presence', priority: 2, category: 'platform' },
    { id: 'regional-presence', label: 'Geographic coverage (regional presence)', priority: 3, category: 'platform' },
    { id: 'management-transition', label: 'Management team or owner transition', priority: 4, category: 'platform' },
    { id: 'payor-diversity', label: 'Payor diversity (multiple contracts)', priority: 5, category: 'platform' },
    { id: 'tech-infrastructure', label: 'Technology infrastructure baseline', priority: 6, category: 'platform' },
    { id: 'growth-potential', label: 'Scalable systems or growth potential', priority: 7, category: 'platform' },

    // Add-on Criteria
    { id: 'revenue-rollup', label: 'Revenue $1M-$5M (add-on size)', priority: 1, category: 'addon' },
    { id: 'single-dual-location', label: 'Single or dual location', priority: 2, category: 'addon' },
    { id: 'specialty-focus', label: 'Specialty focus (anatomic path)', priority: 3, category: 'addon' },
    { id: 'aging-ownership', label: 'Aging ownership (60+ years)', priority: 4, category: 'addon' },
    { id: 'regional-strength', label: 'Strong regional presence', priority: 5, category: 'addon' },
    { id: 'stable-referrals', label: 'Stable referral base', priority: 6, category: 'addon' },
  ],
};

// MSO filter definitions
export const msoFilters = [
  // Investment Strategy Filter (top priority)
  { id: 'mso-targets', label: 'MSO Target Practices', priority: 0, category: 'strategy' },

  // Traditional MSO Criteria
  { id: 'specialty-services', label: 'Specialty services (Cardiology, Derm, Ortho, GI)', priority: 1, category: 'criteria' },
  { id: 'aging-owner', label: 'Aging owner transition (60-70 years)', priority: 2, category: 'criteria' },
  { id: 'mature-practice', label: 'Mature practice (10+ years, stable revenue)', priority: 3, category: 'criteria' },
  { id: 'revenue-stability', label: 'Revenue stability ($1.5M-$7M range)', priority: 4, category: 'criteria' },
  { id: 'insurance-networks', label: 'Community insurance network participation', priority: 5, category: 'criteria' },
  { id: 'tech-integration', label: 'Technology integration opportunity', priority: 6, category: 'criteria' },
  { id: 'group-practice', label: 'Group practice potential', priority: 7, category: 'criteria' },
];

// Filter flag mapping: filter ID → filterFlags property name
const PATHOLOGY_FLAG_MAP: Record<string, keyof NonNullable<PathologyLab['filterFlags']>> = {
  'revenue-platform': 'revenuePlatform',
  'multi-location': 'multiLocation',
  'regional-presence': 'regionalPresence',
  'management-transition': 'managementTransition',
  'payor-diversity': 'payorDiversity',
  'tech-infrastructure': 'techInfrastructure',
  'growth-potential': 'growthPotential',
  'revenue-rollup': 'revenueRollup',
  'single-dual-location': 'singleDualLocation',
  'specialty-focus': 'specialtyFocus',
  'aging-ownership': 'agingOwnership',
  'regional-strength': 'regionalStrength',
  'stable-referrals': 'stableReferrals',
};

// Unified pathology filter function — handles both platform and add-on criteria
export const applyPathologyFilters = (
  labs: PathologyLab[],
  filters: string[]
): PathologyLab[] => {
  let filtered = [...labs];

  // Investment Strategy Filters (highest priority, mutually exclusive)
  if (filters.includes('strategy-platform')) {
    filtered = filtered.filter(lab => lab.labType === 'platform');
  } else if (filters.includes('strategy-addon')) {
    filtered = filtered.filter(lab => lab.labType === 'addon');
  }

  // Apply all criteria filters using the flag map
  for (const filterId of filters) {
    const flagKey = PATHOLOGY_FLAG_MAP[filterId];
    if (flagKey) {
      filtered = filtered.filter(lab => lab.filterFlags?.[flagKey]);
    }
  }

  return filtered;
};

// Keep legacy exports for backwards compatibility
export const applyPlatformFilters = applyPathologyFilters;
export const applyRollupFilters = applyPathologyFilters;

// Apply MSO filters
export const applyMSOFilters = (
  practices: MSOPractice[],
  filters: string[]
): MSOPractice[] => {
  let filtered = [...practices];

  // Investment Strategy Filter (highest priority)
  if (filters.includes('mso-targets')) {
    filtered = filtered.filter(p => p.isTarget === true);
  }

  // Traditional MSO Criteria - use filterFlags
  if (filters.includes('specialty-services')) {
    filtered = filtered.filter(p => p.filterFlags?.specialtyServices);
  }

  if (filters.includes('aging-owner')) {
    filtered = filtered.filter(p => p.filterFlags?.agingOwner);
  }

  if (filters.includes('mature-practice')) {
    filtered = filtered.filter(p => p.filterFlags?.maturePractice);
  }

  if (filters.includes('revenue-stability')) {
    filtered = filtered.filter(p => p.filterFlags?.revenueStability);
  }

  if (filters.includes('insurance-networks')) {
    filtered = filtered.filter(p => p.filterFlags?.insuranceNetworks);
  }

  if (filters.includes('tech-integration')) {
    filtered = filtered.filter(p => p.filterFlags?.techIntegration);
  }

  if (filters.includes('group-practice')) {
    filtered = filtered.filter(p => p.filterFlags?.groupPractice);
  }

  return filtered;
};

// Determine path for a lab based on scores
export const determinePath = (lab: PathologyLab): 'platform' | 'rollup' | 'excluded' => {
  if (lab.scores.platformFit >= 75 && lab.revenue >= 1.5 && lab.revenue <= 9) {
    return 'platform';
  }
  if (lab.scores.rollupFit >= 70 && lab.revenue >= 1 && lab.revenue <= 5) {
    return 'rollup';
  }
  return 'excluded';
};
